import { useEffect, useId, useRef, useState } from "react";
import { UPLOAD_LIMITS, type UploadPurpose } from "@townsy/contracts";
import { fetchUpload, isSafeUploadUrl, postUploadFinalize, postUploadIntent, putUploadContent } from "../../api/advertising";
import { PURPOSE_LABEL, UPLOAD_ACCEPT, UPLOAD_BUSY, UploadController, formatBytes, initialUploadState, uploadStatusText, type UploadState } from "../../advertise/uploadMachine";
import { isSameOriginApiImage } from "../../advertise/media";

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("aborted", "AbortError"));
      return;
    }
    const t = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("aborted", "AbortError"));
      },
      { once: true },
    );
  });

async function readHead(file: Blob, bytes: number): Promise<Uint8Array> {
  return new Uint8Array(await file.slice(0, bytes).arrayBuffer());
}

export interface UploadHandle {
  state: UploadState;
  start(file: File): void;
  cancel(): void;
  retry(): void;
  reset(): void;
}

/** One upload slot (logo or billboard image) driven by {@link UploadController} with the real API. */
export function useUpload(purpose: UploadPurpose): UploadHandle {
  const [state, setState] = useState<UploadState>(() => initialUploadState(purpose));
  const ctrlRef = useRef<UploadController | null>(null);
  if (!ctrlRef.current) {
    ctrlRef.current = new UploadController(purpose, {
      intent: postUploadIntent,
      put: putUploadContent,
      finalize: postUploadFinalize,
      get: fetchUpload,
      readHead,
      isSafeUploadUrl,
      sleep,
      onChange: setState,
    });
  }
  const ctrl = ctrlRef.current;
  // A different purpose (the design switched between storefront and billboard) starts a fresh slot.
  useEffect(() => {
    if (ctrl.state.purpose !== purpose) ctrl.reset(purpose);
  }, [purpose, ctrl]);
  useEffect(() => () => ctrl.cancel(), [ctrl]);
  return {
    state,
    start: (file) => void ctrl.start(file),
    cancel: () => ctrl.cancel(),
    retry: () => void ctrl.retry(),
    reset: () => ctrl.reset(),
  };
}

export function UploadField({ upload, disabled = false }: { upload: UploadHandle; disabled?: boolean }) {
  return <UploadFieldView state={upload.state} disabled={disabled} onPick={upload.start} onCancel={upload.cancel} onRetry={upload.retry} />;
}

/** Stateless view (server-rendered in unit tests). */
export function UploadFieldView({
  state,
  disabled,
  onPick,
  onCancel,
  onRetry,
}: {
  state: UploadState;
  disabled: boolean;
  onPick(file: File): void;
  onCancel(): void;
  onRetry(): void;
}) {
  const inputId = useId();
  const statusId = `${inputId}-status`;
  const busy = UPLOAD_BUSY.has(state.phase);
  const limit = UPLOAD_LIMITS[state.purpose];
  const bad = state.phase === "rejected" || state.phase === "rejected_local" || state.phase === "failed" || state.phase === "expired";
  const preview = state.phase === "ready" && isSameOriginApiImage(state.view?.previewUrl) ? state.view!.previewUrl : null;
  return (
    <div className="upload-field" data-testid="upload-field" data-phase={state.phase}>
      <label className="field" htmlFor={inputId}>
        {state.purpose === "logo" ? "Logo image" : "Billboard image"}
        <input
          id={inputId}
          type="file"
          accept={UPLOAD_ACCEPT}
          disabled={disabled || busy}
          aria-describedby={statusId}
          data-testid="upload-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) onPick(file);
          }}
        />
      </label>
      <p className="hint" style={{ margin: 0 }}>
        PNG, JPEG or WebP; at most {formatBytes(limit.maxBytes)} and {limit.maxPixels} px on the longest side.{" "}
        {state.purpose === "logo"
          ? "Townsy pads it into a square sign texture without stretching or recolouring it."
          : "Townsy fits it to the board's wide shape (10 : 4) without stretching it."}{" "}
        Metadata (such as location) is removed. Nothing is public until a reviewer approves it.
      </p>
      {state.phase === "uploading" ? (
        <progress className="upload-progress" max={100} value={Math.round(state.progress * 100)} aria-label={`Uploading the ${PURPOSE_LABEL[state.purpose]}`} />
      ) : null}
      <p
        id={statusId}
        className={`small ${bad ? "error-text" : state.phase === "ready" ? "success-text" : "muted"}`}
        role={bad ? "alert" : "status"}
        aria-live="polite"
        style={{ margin: 0 }}
        data-testid="upload-status"
      >
        {state.fileName && state.phase !== "idle" ? <span className="mono">{state.fileName}: </span> : null}
        {uploadStatusText(state)}
      </p>
      {preview ? (
        <img className="upload-preview" src={preview} alt={`Private preview of your ${PURPOSE_LABEL[state.purpose]} after Townsy's re-encoding`} />
      ) : null}
      <div className="row">
        {busy && state.phase !== "processing" ? (
          <button type="button" className="btn btn-sm" onClick={onCancel}>
            Cancel upload
          </button>
        ) : null}
        {!busy && state.retryable ? (
          <button type="button" className="btn btn-sm" onClick={onRetry} data-testid="upload-retry">
            {state.phase === "failed" && state.view?.status === "processing" ? "Check again" : "Try again"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
